import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, ButtonGroup, Card, Checkbox, Col, Form, Grid, Heading, Input, Label, Select, Spinner, Stack, Table, Td,
  Text, Th, Tr,
} from '@ui'
import { useSearchParams } from 'react-router-dom'
import { getJSON, putJSON } from '../api.js'
import HistoryChart from '../components/HistoryChart.jsx'
import { GoalsSection } from '../components/WeeklyGoals.jsx'
import YearChart, { aheadText, fmt } from '../components/YearChart.jsx'
import { addDays, dateKey, dayOfYear, daysInMonth, daysInYear, todayKey, utcDate } from '../dates.js'
import { UNITS, ukDateKey } from '../format.js'
import { useStoredState } from '../storage.js'
import { useUnit } from '../units.jsx'

// Targets (M4): how the year is going against its distance target, in place of the hand-kept
// spreadsheet. Totals count each walk once (walk_groups), as the calendar and monthly totals do.
// Monthly targets are the annual one shared out by days (decided 2026-10-02), so "on pace" is
// measured to today. Today counts as walked: the rest-of-period figures start tomorrow.

// Targets start in 2026 (decided 2026-10-02).
const TARGETS_FROM = 2026
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December']
// Quick picks for the projection rate, like the spreadsheet's Max (3), (4), (5) columns.
const RATE_PRESETS = { mi: [3, 4, 5], km: [5, 6, 8] }

function yearModel(year, daily, target, today) {
  const days = daysInYear(year)
  const thisYear = today.getUTCFullYear()
  const elapsed = year < thisYear ? days : year > thisYear ? 0 : dayOfYear(today)
  const jan1 = new Date(Date.UTC(year, 0, 1))
  const monthStarts = MONTHS.map((_, m) => dayOfYear(new Date(Date.UTC(year, m, 1))) - 1)
  const months = MONTHS.map((name, m) => {
    const dm = daysInMonth(year, m)
    return {
      m, name, days: dm,
      elapsed: Math.min(dm, Math.max(0, elapsed - monthStarts[m])),
      distance: 0,
      target: target ? (target * dm) / days : null,
    }
  })
  const cumulative = []
  let run = 0
  for (let i = 0; i < elapsed; i++) {
    const date = addDays(jan1, i)
    const meters = daily.get(dateKey(date)) ?? 0
    run += meters
    cumulative.push(run)
    months[date.getUTCMonth()].distance += meters
  }
  return { year, days, elapsed, cumulative, target, monthStarts, months, total: run }
}

export default function Targets() {
  const [unit] = useUnit()
  const [params, setParams] = useSearchParams()
  const [walks, setWalks] = useState(null)
  const [targets, setTargets] = useState(null)
  const [error, setError] = useState(null)
  const [rate, setRate] = useStoredState('targets.rateMeters', 4 * UNITS.mi.meters)

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([getJSON('/api/walks', { signal: controller.signal }), getJSON('/api/targets', { signal: controller.signal })])
      .then(([w, t]) => { setWalks(w); setTargets(t) })
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    return () => controller.abort()
  }, [])

  // Metres walked per UK calendar day, each walk counted once.
  const daily = useMemo(() => {
    const days = new Map()
    for (const w of walks ?? []) {
      if (!w.is_counted) continue
      const key = ukDateKey(w.start_time)
      days.set(key, (days.get(key) ?? 0) + w.distance_meters)
    }
    return days
  }, [walks])

  // The home page's "This week" links to #brisk, which exists only once the walks are in.
  useEffect(() => {
    if (walks && window.location.hash === '#brisk') document.getElementById('brisk')?.scrollIntoView()
  }, [walks])

  if (error) return <Alert variant="danger">Couldn't load walks: {error}</Alert>
  if (!walks || !targets) return <Spinner />

  const today = utcDate(todayKey())
  const thisYear = today.getUTCFullYear()
  const keys = [...daily.keys()].sort()
  const firstYear = keys.length ? Number(keys[0].slice(0, 4)) : thisYear
  const lastYear = Math.max(thisYear + 1, TARGETS_FROM)
  const requested = Number(params.get('year'))
  const year = requested >= firstYear && requested <= lastYear ? requested : thisYear
  const targetOf = (y) => targets.find((t) => t.year === y)?.distance_meters ?? null
  const model = yearModel(year, daily, targetOf(year), today)
  const u = UNITS[unit].meters
  const allTime = [...daily.values()].reduce((s, m) => s + m, 0)
  const projection = model.elapsed < model.days
    ? { rate, end: model.total + rate * (model.days - model.elapsed) }
    : null
  const goTo = (y) => setParams(y === thisYear ? {} : { year: String(y) })

  return (
    <>
      <Stack wrap align="center" gap={2} mb={1}>
        <Heading size={3} mb={0} me={2}>Targets · {year}</Heading>
        <ButtonGroup size="sm">
          <Button variant="secondary" outline disabled={year <= firstYear} onClick={() => goTo(year - 1)} aria-label="Previous year">‹</Button>
          <Button variant="secondary" outline disabled={year >= lastYear} onClick={() => goTo(year + 1)} aria-label="Next year">›</Button>
        </ButtonGroup>
        {year !== thisYear && <Button size="sm" variant="secondary" outline onClick={() => goTo(thisYear)}>This year</Button>}
      </Stack>
      <Text as="p" muted text="small" mb={3}>
        All time: {fmt(allTime / u, 1)} {UNITS[unit].label} since {firstYear}. Each walk counted once, by its UK date.
      </Text>

      <TargetEditor year={year} target={model.target} unit={unit} onSaved={setTargets} />
      <Summary model={model} unit={unit} projection={projection} rate={rate} setRate={setRate} />
      {model.elapsed > 0 && model.elapsed < model.days && <ThisMonth model={model} today={today} unit={unit} rate={rate} />}
      {year === thisYear && <GoalsSection walks={walks} />}

      <Heading level={2} size={5} mt={4}>Through the year</Heading>
      <YearChart model={model} unit={unit} projection={projection} />

      <Heading level={2} size={5} mt={4}>Months</Heading>
      <MonthTable model={model} unit={unit} projection={projection} rate={rate} />

      <History daily={daily} targetOf={targetOf} firstYear={firstYear} today={today} unit={unit} />
    </>
  )
}

function TargetEditor({ year, target, unit, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const u = UNITS[unit].meters
  if (year < TARGETS_FROM) return null

  const save = (meters) => {
    setBusy(true)
    setError(null)
    putJSON(`/api/targets/${year}`, { distance_meters: meters })
      .then((t) => { onSaved(t); setEditing(false) })
      .catch((err) => setError(err.message))
      .finally(() => setBusy(false))
  }
  const start = () => { setValue(target ? String(Math.round((target / u) * 10) / 10) : ''); setEditing(true) }
  const parsed = Number(value)

  if (!editing) {
    return (
      <Box as="p" mb={3}>
        {target
          ? <>Target for {year}: <strong>{fmt(target / u)} {UNITS[unit].label}</strong> <Button size="sm" variant="link" p={0} ms={2} valign="baseline" onClick={start}>Change</Button></>
          : <>No target for {year} yet. <Button size="sm" variant="link" p={0} valign="baseline" onClick={start}>Set a target</Button></>}
      </Box>
    )
  }
  return (
    <Form
      display="flex" wrap align="center" gap={2} mb={3}
      onSubmit={(e) => { e.preventDefault(); if (parsed > 0) save(parsed * u) }}
    >
      <Label htmlFor="target-input" mb={0}>Target for {year}</Label>
      <Input
        size="sm" width={160} suffix={UNITS[unit].label}
        id="target-input" type="number" min="1" step="any" value={value} autoFocus
        onChange={(e) => setValue(e.target.value)}
      />
      <Button size="sm" type="submit" disabled={busy || !(parsed > 0)}>Save</Button>
      {target && <Button size="sm" variant="danger" outline disabled={busy} onClick={() => save(null)}>Remove</Button>}
      <Button size="sm" variant="secondary" outline disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
      {error && <Text tone="danger" text="small">{error}</Text>}
    </Form>
  )
}

function Tile({ label, value, children }) {
  return (
    <Col xs={6} lg={3} mb={3}>
      <Box muted text="small">{label}</Box>
      <Box text="xl" weight="semibold" tight>{value}</Box>
      {children && <Box text="small" muted>{children}</Box>}
    </Col>
  )
}

function Summary({ model, unit, projection, rate, setRate }) {
  const u = UNITS[unit].meters
  const label = UNITS[unit].label
  const { total, target, elapsed, days, year } = model
  const daysLeft = days - elapsed
  const average = elapsed ? total / elapsed : 0
  const best = model.months.reduce((b, m) => (m.distance > (b?.distance ?? 0) ? m : b), null)

  return (
    <Grid mb={2}>
      <Tile label={elapsed < days ? 'Walked so far' : 'Walked'} value={`${fmt(total / u, 1)} ${label}`}>
        {target ? `${fmt((total / target) * 100, 1)}% of ${fmt(target / u)} ${label}` : `${fmt(average / u, 2)} ${label} a day`}
      </Tile>
      {target && elapsed < days && elapsed > 0 && (
        <Tile label="Against target pace" value={aheadText(total - (target * elapsed) / days, unit)}>
          Target to date {fmt((target * elapsed) / days / u, 1)} {label}
        </Tile>
      )}
      {target && elapsed === days && (
        <Tile label="Target" value={total >= target ? 'Reached' : 'Missed'}>
          {aheadText(total - target, unit)}
        </Tile>
      )}
      {target && daysLeft > 0 && (
        <Tile label="Needed a day from tomorrow"
          value={total >= target ? 'Done' : `${fmt((target - total) / daysLeft / u, 2)} ${label}`}>
          {elapsed > 0 ? `Averaging ${fmt(average / u, 2)} ${label} a day so far` : `${daysLeft} days`}
        </Tile>
      )}
      {!target && elapsed > 0 && best && (
        <Tile label="Best month" value={`${fmt(best.distance / u, 1)} ${label}`}>{best.name} {year}</Tile>
      )}
      {projection && (
        <Col xs={12} lg={target ? 3 : 6} mb={3}>
          <Box muted text="small">Projected for {year}</Box>
          <Box text="xl" weight="semibold" tight>{fmt(projection.end / u, 1)} {label}</Box>
          <Box text="small" muted>
            {target && <>{aheadText(projection.end - target, unit)}, </>}if you walk
          </Box>
          <RateInput rate={rate} setRate={setRate} unit={unit} />
        </Col>
      )}
    </Grid>
  )
}

// The daily rate behind every projection: typed in, or one of the quick picks. Remembered per
// viewer, in metres, so it survives switching units.
function RateInput({ rate, setRate, unit }) {
  const u = UNITS[unit].meters
  const shown = Math.round((rate / u) * 10) / 10
  const [text, setText] = useState(String(shown))
  useEffect(() => { setText(String(Math.round((rate / u) * 10) / 10)) }, [rate, u])
  return (
    <Stack wrap align="center" gap={1} mt={1}>
      <Input
        size="sm" width={130} suffix={`${UNITS[unit].label}/day`}
        type="number" min="0" step="0.5" value={text} aria-label={`Daily distance in ${UNITS[unit].label}`}
        onChange={(e) => {
          setText(e.target.value)
          const v = Number(e.target.value)
          if (e.target.value !== '' && v >= 0 && v < 100) setRate(v * u)
        }}
      />
      <ButtonGroup size="sm" aria-label="Quick picks">
        {RATE_PRESETS[unit].map((p) => (
          <Button key={p} variant="secondary" outline={Math.abs(shown - p) >= 0.01} onClick={() => setRate(p * u)}>{p}</Button>
        ))}
      </ButtonGroup>
    </Stack>
  )
}

function ThisMonth({ model, today, unit, rate }) {
  const u = UNITS[unit].meters
  const label = UNITS[unit].label
  const month = model.months[today.getUTCMonth()]
  const left = month.days - month.elapsed
  const monthEnd = model.monthStarts[month.m] + month.days
  // Where the year should be by the end of this month to be on pace (the spreadsheet's K15/K17).
  const yearPaceByMonthEnd = model.target ? (model.target * monthEnd) / model.days : null
  const perDay = (meters) => (left > 0 ? `${fmt(Math.max(0, meters) / left / u, 2)} ${label} a day` : '–')
  return (
    <Card compact mb={3} title={<>{month.name}: {left} {left === 1 ? 'day' : 'days'} left after today</>}>
      <Grid text="small">
        <Col sm={6} lg={3}>Walked <strong>{fmt(month.distance / u, 1)} {label}</strong> ({fmt(month.distance / month.elapsed / u, 2)} a day)</Col>
        {month.target && (
          <Col sm={6} lg={3}>
            Month target {fmt(month.target / u, 1)} {label}: {month.distance >= month.target ? 'reached' : `needs ${perDay(month.target - month.distance)}`}
          </Col>
        )}
        {yearPaceByMonthEnd && (
          <Col sm={6} lg={3}>
            Back on year pace by {today.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short' })} {month.days}:{' '}
            {model.total >= yearPaceByMonthEnd ? 'already there' : `needs ${perDay(yearPaceByMonthEnd - model.total)}`}
          </Col>
        )}
        <Col sm={6} lg={3}>
          At {fmt(rate / u, 1)} {label} a day: {fmt((month.distance + rate * left) / u, 1)} {label} by the month's end
        </Col>
      </Grid>
    </Card>
  )
}

function MonthTable({ model, unit, projection, rate }) {
  const u = UNITS[unit].meters
  const d = (meters, digits = 1) => (meters == null ? '–' : fmt(meters / u, digits))
  const signed = (meters) => {
    if (meters == null) return '–'
    const v = Math.round((meters / u) * 10) / 10
    return v === 0 ? '0.0' : `${v > 0 ? '+' : '−'}${fmt(Math.abs(v), 1)}`
  }
  let projectedTotal = 0
  const rows = model.months.map((m) => {
    const started = m.elapsed > 0
    const complete = m.elapsed === m.days
    const diff = m.target && started ? m.distance - (m.target * m.elapsed) / m.days : null
    const projected = projection ? m.distance + rate * (m.days - m.elapsed) : null
    projectedTotal += projected ?? 0
    return { ...m, started, complete, diff, projected }
  })
  const totalDiff = model.target && model.elapsed ? model.total - (model.target * model.elapsed) / model.days : null
  return (
    <Table hover responsive middle>
      <thead>
        <tr>
          <th>Month</th>
          <Th numeric>Walked ({UNITS[unit].label})</Th>
          <Th numeric hideBelow="sm">Per day</Th>
          {model.target && <Th numeric>Target</Th>}
          {model.target && <Th numeric>±</Th>}
          {projection && <Th numeric>At {fmt(rate / u, 1)}/day</Th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((m) => (
          <Tr key={m.m} active={m.started && !m.complete}>
            <td>{m.name}{m.started && !m.complete && <Text muted text="small"> (to date)</Text>}</td>
            <Td numeric>{m.started ? d(m.distance, 2) : ''}</Td>
            <Td numeric hideBelow="sm">{m.started ? d(m.distance / m.elapsed, 2) : ''}</Td>
            {model.target && <Td numeric>{d(m.target)}</Td>}
            {model.target && (
              <Td numeric tone={m.diff == null ? undefined : m.diff >= 0 ? 'good' : 'bad'}>
                {m.diff == null ? '' : signed(m.diff)}
              </Td>
            )}
            {projection && <Td numeric muted={m.complete}>{d(m.projected)}</Td>}
          </Tr>
        ))}
      </tbody>
      <tfoot>
        <Tr weight="semibold">
          <td>{model.year}</td>
          <Td numeric>{d(model.total, 2)}</Td>
          <Td numeric hideBelow="sm">{model.elapsed ? d(model.total / model.elapsed, 2) : ''}</Td>
          {model.target && <Td numeric>{d(model.target)}</Td>}
          {model.target && (
            <Td numeric tone={totalDiff == null ? undefined : totalDiff >= 0 ? 'good' : 'bad'}>
              {totalDiff == null ? '' : signed(totalDiff)}
            </Td>
          )}
          {projection && <Td numeric>{d(projectedTotal)}</Td>}
        </Tr>
      </tfoot>
    </Table>
  )
}

const SHORT_MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

function History({ daily, targetOf, firstYear, today, unit }) {
  const thisYear = today.getUTCFullYear()
  const [stored, setStored] = useStoredState('targets.history', { group: 'year', from: null, to: null })
  const [showTable, setShowTable] = useState(false)
  const years = []
  for (let y = firstYear; y <= thisYear; y++) years.push(y)
  const clampYear = (y, fallback) => (years.includes(y) ? y : fallback)
  const from = clampYear(stored.from, firstYear)
  const to = Math.max(from, clampYear(stored.to, thisYear))
  const group = stored.group === 'month' ? 'month' : 'year'
  const set = (patch) => setStored({ group, from, to, ...patch })
  const u = UNITS[unit].meters

  const byMonth = new Map()
  for (const [key, meters] of daily) byMonth.set(key.slice(0, 7), (byMonth.get(key.slice(0, 7)) ?? 0) + meters)
  const rows = []
  for (let y = from; y <= to; y++) {
    const target = targetOf(y)
    if (group === 'year') {
      let meters = 0
      for (let m = 0; m < 12; m++) meters += byMonth.get(`${y}-${String(m + 1).padStart(2, '0')}`) ?? 0
      rows.push({ key: String(y), label: String(y), axisLabel: String(y), meters, target, partial: y === thisYear })
      continue
    }
    for (let m = 0; m < 12; m++) {
      if (y === thisYear && m > today.getUTCMonth()) break
      const key = `${y}-${String(m + 1).padStart(2, '0')}`
      rows.push({
        key,
        label: `${MONTHS[m]} ${y}`,
        axisLabel: m === 0 || rows.length === 0 ? String(y) : '',
        minorLabel: SHORT_MONTHS[m],
        meters: byMonth.get(key) ?? 0,
        target: target ? (target * daysInMonth(y, m)) / daysInYear(y) : null,
        partial: y === thisYear && m === today.getUTCMonth(),
      })
    }
  }

  return (
    <>
      <Heading level={2} size={5} mt={4}>History</Heading>
      <Stack wrap align="center" gap={2} mb={2}>
        <ButtonGroup size="sm" aria-label="Group by">
          <Button variant="secondary" outline={group !== 'year'} onClick={() => set({ group: 'year' })}>By year</Button>
          <Button variant="secondary" outline={group !== 'month'} onClick={() => set({ group: 'month' })}>By month</Button>
        </ButtonGroup>
        <Label htmlFor="history-from" mb={0} text="small" ms={2}>From</Label>
        <Select id="history-from" size="sm" style={{ width: 'auto' }} value={from}
          onChange={(e) => { const f = Number(e.target.value); set({ from: f, to: Math.max(f, to) }) }}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
        <Label htmlFor="history-to" mb={0} text="small">to</Label>
        <Select id="history-to" size="sm" style={{ width: 'auto' }} value={to}
          onChange={(e) => { const t = Number(e.target.value); set({ to: t, from: Math.min(from, t) }) }}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
        <Checkbox toggle id="history-table" ms={2} text="small" label="Table" checked={showTable}
          onChange={(e) => setShowTable(e.target.checked)} />
      </Stack>
      <HistoryChart rows={rows} unit={unit} />
      {showTable && (
        <Table hover responsive mt={3}>
          <thead>
            <tr>
              <th>{group === 'year' ? 'Year' : 'Month'}</th>
              <Th numeric>Walked ({UNITS[unit].label})</Th>
              <Th numeric>Target</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}{r.partial ? ' (so far)' : ''}</td>
                <Td numeric>{fmt(r.meters / u, 2)}</Td>
                <Td numeric>{r.target ? fmt(r.target / u, 1) : '–'}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  )
}
